import { forwardRef, useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertColor, AlertProps } from "@mui/material/Alert";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setSnackBarOpen } from "../../features/snackbar/snackbar";

interface CustomizedSnackbarProps {
  message: string;
  severity: string;
  open: boolean;
  details?: string;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function CustomizedSnackbar({
  message,
  open,
  severity,
  details,
}: CustomizedSnackbarProps) {
  const dispatch = useAppDispatch();
  const position = useAppSelector(
    (state) => state.snackbar.snackbarData.position
  );
  const [expanded, setExpanded] = useState(true);

  // Cada snackbar novo abre com os detalhes ja expandidos. Se o usuario
  // fechar a setinha durante a exibicao, ela permanece fechada ate o
  // proximo snackbar abrir.
  useEffect(() => {
    if (open) setExpanded(true);
  }, [open]);

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(setSnackBarOpen(false));
    setExpanded(false);
  };

  const hasDetails = !!details && details.length > 0 && (severity === "error" || severity === "warning");

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Snackbar
        anchorOrigin={{
          vertical: position === "top" ? "top" : "bottom",
          horizontal: "center",
        }}
        open={open}
        autoHideDuration={hasDetails ? 10000 : 6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={severity as AlertColor}
          sx={{
            width: "100%",
            minWidth: "320px",
            maxWidth: "500px",
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              {message}
            </Typography>
            {hasDetails && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                sx={{
                  color: "inherit",
                  padding: "2px",
                  marginLeft: "8px",
                }}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Box>
          {hasDetails && (
            <Collapse in={expanded} timeout={200}>
              <Typography
                sx={{
                  fontSize: "12px",
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "rgba(0, 0, 0, 0.15)",
                  borderRadius: "4px",
                  lineHeight: 1.4,
                }}
              >
                {details}
              </Typography>
            </Collapse>
          )}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
